import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LanguageSelector } from '../components/LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { 
  Zap, 
  Droplets, 
  MapPin, 
  Download, 
  Users, 
  Shield, 
  Clock,
  Phone,
  Mail,
  ChevronRight,
  Menu,
  X,
  Battery,
  Sun,
  Wrench,
  Facebook,
  Youtube,
  Instagram
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APK_URL = "https://customer-assets.emergentagent.com/job_outrage-reporter-app/artifacts/8ougha0a_SafiEco%201.0.3.apk";

export const LandingPage = () => {
  const { t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState({ electricity_count: 0, water_count: 0 });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/outages/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleDownloadAPK = () => {
    window.open(APK_URL, '_blank');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-blur bg-background/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 text-primary">
                <Zap className="h-5 w-5" strokeWidth={2.5} />
                <Droplets className="h-5 w-5 text-blue-500" strokeWidth={2.5} />
              </div>
              <span className="font-bold text-xl tracking-tight">Safi Energy</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t('nav_application')}
              </a>
              <a href="#services" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t('nav_services')}
              </a>
              <a href="#contact" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t('nav_contact')}
              </a>
            </div>

            {/* Auth Buttons & Language */}
            <div className="hidden md:flex items-center gap-4">
              <LanguageSelector variant="ghost" />
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2">
              <LanguageSelector variant="ghost" />
              <button 
                className="p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-card border-t border-border">
            <div className="px-4 py-4 space-y-4">
              <a href="#features" className="block text-sm font-medium text-muted-foreground">{t('nav_application')}</a>
              <a href="#services" className="block text-sm font-medium text-muted-foreground">{t('nav_services')}</a>
              <a href="#contact" className="block text-sm font-medium text-muted-foreground">{t('nav_contact')}</a>
              <Button 
                onClick={() => { handleDownloadAPK(); setMobileMenuOpen(false); }}
                className="w-full bg-primary text-primary-foreground font-bold gap-2"
              >
                <Download className="h-4 w-4" />
                {t('hero_download')}
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-bg pt-32 pb-20 lg:pb-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 rounded-sm">
                <MapPin className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">{t('hero_location')}</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight uppercase leading-none">
                {t('hero_title_1')}<br />
                <span className="text-primary">{t('hero_title_2')}</span><br />
                {t('hero_title_3')}
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-lg">
                {t('hero_description')}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={handleDownloadAPK}
                  className="h-14 px-8 bg-primary text-primary-foreground font-bold uppercase tracking-wider text-base gap-3 btn-glow"
                  data-testid="download-apk-btn"
                >
                  <Download className="h-5 w-5" />
                  {t('hero_download')}
                </Button>
              </div>
              
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-secondary rounded-full animate-pulse"></span>
                {t('hero_ios_soon')}
              </p>
            </div>

            {/* Phone Mockup */}
            <div className="relative flex justify-center lg:justify-end animate-fade-in animation-delay-200">
              <div className="phone-mockup w-[280px] sm:w-[320px]">
                <div className="bg-card rounded-[2rem] overflow-hidden">
                  {/* Phone Content */}
                  <div className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-lg">Safi Energy</h3>
                        <p className="text-xs text-muted-foreground">Lubumbashi</p>
                      </div>
                      <span className="text-xs font-medium text-muted-foreground">FR</span>
                    </div>
                    
                    {/* Stats Cards */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-muted/50 rounded-xl p-4 border border-border">
                        <div className="flex items-center gap-2 mb-1">
                          <Zap className="h-4 w-4 text-primary" />
                          <span className="text-2xl font-bold">{stats.electricity_count}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{t('dashboard_electricity')}</p>
                      </div>
                      <div className="bg-muted/50 rounded-xl p-4 border border-border">
                        <div className="flex items-center gap-2 mb-1">
                          <Droplets className="h-4 w-4 text-blue-500" />
                          <span className="text-2xl font-bold">{stats.water_count}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{t('dashboard_water')}</p>
                      </div>
                    </div>
                    
                    {/* Map Preview */}
                    <div className="h-40 bg-muted/30 rounded-xl flex items-center justify-center border border-border">
                      <MapPin className="h-8 w-8 text-muted-foreground" />
                    </div>

                    {/* Bottom Navigation */}
                    <div className="flex items-center justify-center gap-8 pt-2">
                      <button className="p-2 text-muted-foreground">
                        <MapPin className="h-5 w-5" />
                      </button>
                      <button className="p-4 bg-primary rounded-full">
                        <span className="text-primary-foreground font-bold text-xl">+</span>
                      </button>
                      <button className="p-2 text-muted-foreground">
                        <Users className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-32 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase mb-4">
              {t('features_title')} <span className="text-primary">{t('features_title_highlight')}</span> ?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('features_subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Feature 1 */}
            <div className="bg-background border border-border rounded-xl p-6 card-hover" data-testid="feature-realtime">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <Clock className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('feature_realtime')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('feature_realtime_desc')}
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-background border border-border rounded-xl p-6 card-hover" data-testid="feature-community">
              <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-secondary" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('feature_community')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('feature_community_desc')}
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-background border border-border rounded-xl p-6 card-hover" data-testid="feature-map">
              <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
                <MapPin className="h-6 w-6 text-blue-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('feature_map')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('feature_map_desc')}
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-background border border-border rounded-xl p-6 card-hover" data-testid="feature-secure">
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-accent" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('feature_reliable')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('feature_reliable_desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase mb-4">
              {t('services_title')} <span className="text-secondary">{t('services_title_highlight')}</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('services_subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Service 1 */}
            <div className="service-card border border-border rounded-xl p-8 card-hover" data-testid="service-batteries">
              <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                <Battery className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-3 uppercase tracking-wide">{t('service_batteries')}</h3>
              <p className="text-muted-foreground mb-4">
                {t('service_batteries_desc')}
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <ChevronRight className="h-4 w-4 text-primary" />
                  <span>{t('service_batteries_feature1')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <ChevronRight className="h-4 w-4 text-primary" />
                  <span>{t('service_batteries_feature2')}</span>
                </li>
              </ul>
            </div>

            {/* Service 2 */}
            <div className="service-card border border-border rounded-xl p-8 card-hover" data-testid="service-panels">
              <div className="w-16 h-16 bg-secondary/10 rounded-xl flex items-center justify-center mb-6">
                <Sun className="h-8 w-8 text-secondary" />
              </div>
              <h3 className="text-2xl font-bold mb-3 uppercase tracking-wide">{t('service_panels')}</h3>
              <p className="text-muted-foreground mb-4">
                {t('service_panels_desc')}
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <ChevronRight className="h-4 w-4 text-secondary" />
                  <span>{t('service_panels_feature1')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <ChevronRight className="h-4 w-4 text-secondary" />
                  <span>{t('service_panels_feature2')}</span>
                </li>
              </ul>
            </div>

            {/* Service 3 */}
            <div className="service-card border border-border rounded-xl p-8 card-hover" data-testid="service-installation">
              <div className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center mb-6">
                <Wrench className="h-8 w-8 text-accent" />
              </div>
              <h3 className="text-2xl font-bold mb-3 uppercase tracking-wide">{t('service_installation')}</h3>
              <p className="text-muted-foreground mb-4">
                {t('service_installation_desc')}
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <ChevronRight className="h-4 w-4 text-accent" />
                  <span>{t('service_installation_feature1')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <ChevronRight className="h-4 w-4 text-accent" />
                  <span>{t('service_installation_feature2')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-card border-y border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-6">
            {t('cta_title')} <span className="text-primary">{t('cta_title_highlight')}</span>
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            {t('cta_description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleDownloadAPK}
              className="h-14 px-8 bg-primary text-primary-foreground font-bold uppercase tracking-wider text-base gap-3 btn-glow"
              data-testid="cta-download-btn"
            >
              <Download className="h-5 w-5" />
              {t('cta_download')}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            {t('hero_ios_soon')}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase mb-6">
                <span className="text-primary">{t('contact_title')}</span>{t('contact_title_suffix')}
              </h2>
              <p className="text-muted-foreground mb-8">
                {t('contact_description')}
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">{t('contact_address')}</h4>
                    <p className="text-muted-foreground">
                      Plateau Karavia, AV la source N°34<br />
                      Lubumbashi, RDC
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="h-5 w-5 text-secondary" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">{t('contact_phone')}</h4>
                    <a href="tel:+243993268952" className="text-muted-foreground hover:text-primary transition-colors">
                      +243 993 268 952
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">{t('contact_email')}</h4>
                    <div className="space-y-1">
                      <a href="mailto:info@safienergy.org" className="block text-muted-foreground hover:text-primary transition-colors">
                        info@safienergy.org
                      </a>
                      <a href="mailto:projet@safienergy.org" className="block text-muted-foreground hover:text-primary transition-colors">
                        projet@safienergy.org
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-6 uppercase tracking-wide">{t('contact_zone_title')}</h3>
              <p className="text-muted-foreground mb-6">
                {t('contact_zone_description')}
              </p>
              <ul className="space-y-3">
                {['Lubumbashi', 'Likasi', 'Kasumbalesa', 'Province du Lualaba (Kolwezi)'].map((location) => (
                  <li key={location} className="flex items-center gap-3">
                    <ChevronRight className="h-5 w-5 text-primary" />
                    <span className="font-medium">{location}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-8 pt-8 border-t border-border">
                <h4 className="font-bold mb-4">{t('contact_follow')}</h4>
                <div className="flex gap-4">
                  <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" 
                     className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center hover:bg-primary/10 transition-colors">
                    <Facebook className="h-5 w-5" />
                  </a>
                  <a href="https://youtube.com" target="_blank" rel="noopener noreferrer"
                     className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center hover:bg-primary/10 transition-colors">
                    <Youtube className="h-5 w-5" />
                  </a>
                  <a href="https://instagram.com" target="_blank" rel="noopener noreferrer"
                     className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center hover:bg-primary/10 transition-colors">
                    <Instagram className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                <span className="font-bold">Safi Energy</span>
                <span className="text-muted-foreground text-sm">- {t('footer_tagline')}</span>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
                <Link 
                  to="/privacy-policy"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {t('footer_privacy')}
                </Link>
                <Link 
                  to="/terms-and-conditions"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {t('footer_terms')}
                </Link>
                <Link 
                  to="/delete-account"
                  className="text-sm text-muted-foreground hover:text-destructive transition-colors"
                >
                  {t('footer_delete')}
                </Link>
              </div>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                {t('footer_copyright')}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
